import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Camera, X, CheckCircle, Star } from 'lucide-react';
import { toast } from 'sonner';

const WorkOrderCompletionForm = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workOrderId = searchParams.get('workOrderId');
  
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Signature canvas
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    problem_description: '',
    resolution_summary: '',
    materials_used: '',
    is_billable: true,
    customer_representative_name: '',
    signature_status: 'Signed by Customer Representative',
    satisfaction_rating: 0,
    satisfaction_comments: '',
    field_user_notes: ''
  });
  
  // Photo state
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [beforePhotoURLs, setBeforePhotoURLs] = useState([]);
  const [afterPhotoURLs, setAfterPhotoURLs] = useState([]);
  
  // Validation errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (workOrderId) {
      fetchWorkOrder();
    }
  }, [workOrderId, token]);

  useEffect(() => {
    // Initialize canvas
    if (signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, []);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/work-orders/${workOrderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch work order:', error);
      toast.error('Failed to load work order');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  // Signature canvas handlers
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.beginPath();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // Photo handlers
  const handleBeforePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    setBeforePhotos([...beforePhotos, ...files]);
    
    // Create preview URLs
    const urls = files.map(file => URL.createObjectURL(file));
    setBeforePhotoURLs([...beforePhotoURLs, ...urls]);
  };

  const handleAfterPhotoChange = (e) => {
    const files = Array.from(e.target.files);
    setAfterPhotos([...afterPhotos, ...files]);
    
    // Create preview URLs
    const urls = files.map(file => URL.createObjectURL(file));
    setAfterPhotoURLs([...afterPhotoURLs, ...urls]);
  };

  const removeBeforePhoto = (index) => {
    setBeforePhotos(beforePhotos.filter((_, i) => i !== index));
    setBeforePhotoURLs(beforePhotoURLs.filter((_, i) => i !== index));
  };

  const removeAfterPhoto = (index) => {
    setAfterPhotos(afterPhotos.filter((_, i) => i !== index));
    setAfterPhotoURLs(afterPhotoURLs.filter((_, i) => i !== index));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.problem_description.trim()) {
      newErrors.problem_description = 'Problem description is required';
    }
    
    if (!formData.resolution_summary.trim()) {
      newErrors.resolution_summary = 'Resolution summary is required';
    }
    
    if (beforePhotos.length === 0) {
      newErrors.before_photos = 'At least one before photo is required';
    }
    
    if (afterPhotos.length === 0) {
      newErrors.after_photos = 'At least one after photo is required';
    }
    
    if (formData.signature_status === 'Signed by Customer Representative' && !hasSignature) {
      newErrors.signature = 'Please capture customer signature or change signature status';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      
      // Show specific error for photos
      if (beforePhotos.length === 0 || afterPhotos.length === 0) {
        toast.error('Before and after photos are required for all work performed. Please upload at least one photo for each.');
      }
      return;
    }
    
    try {
      setSubmitting(true);
      
      // For now, use placeholder URLs for photos
      // TODO: Implement actual photo upload to storage
      const beforePhotoRefs = beforePhotos.map((_, i) => `/uploads/work-order-photos/before-${Date.now()}-${i}.jpg`);
      const afterPhotoRefs = afterPhotos.map((_, i) => `/uploads/work-order-photos/after-${Date.now()}-${i}.jpg`);
      
      // Convert signature to image if present
      let signatureURL = null;
      if (hasSignature) {
        const canvas = signatureCanvasRef.current;
        signatureURL = canvas.toDataURL('image/png');
        // TODO: Upload signature to storage and get URL
        signatureURL = `/uploads/signatures/signature-${Date.now()}.png`;
      }
      
      // Prepare completion data
      const completionData = {
        work_order_id: workOrderId,
        work_order_number: workOrder.tfs_work_order_number,
        service_location: workOrder.service_location,
        field_user_name: user.name,
        field_user_id: user.id,
        problem_description: formData.problem_description,
        resolution_summary: formData.resolution_summary,
        materials_used: formData.materials_used,
        is_billable: formData.is_billable,
        before_photos: beforePhotoRefs,
        after_photos: afterPhotoRefs,
        customer_representative_name: formData.customer_representative_name,
        customer_signature_url: signatureURL,
        signature_status: formData.signature_status,
        field_user_notes: formData.field_user_notes
      };
      
      // Submit completion
      const response = await axios.post(
        `${API}/work-order-completions`,
        completionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // If satisfaction rating provided, create feedback record
      if (formData.satisfaction_rating > 0) {
        // TODO: Create client feedback record
        // This will be implemented when we build the Client Feedback module
      }
      
      toast.success('Work order completed successfully!');
      navigate('/field/work-orders');
      
    } catch (error) {
      console.error('Failed to submit completion:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit work order completion');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading work order...</div>;
  }

  if (!workOrder) {
    return <div className="p-4 text-center">Work order not found</div>;
  }

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Complete Work Order</CardTitle>
          <p className="text-sm text-gray-600">
            Work Order #{workOrder.tfs_work_order_number} - {workOrder.service_location}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Work Order Summary (Read-only) */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Work Order Details</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Customer:</span>
                  <div className="font-medium">{workOrder.customer_name}</div>
                </div>
                <div>
                  <span className="text-gray-600">Location:</span>
                  <div className="font-medium">{workOrder.service_location}</div>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Original Request:</span>
                  <div className="font-medium">{workOrder.description_of_work}</div>
                </div>
              </div>
            </div>

            {/* Problem Description */}
            <div>
              <Label htmlFor="problem_description">Problem Found / Description *</Label>
              <Textarea
                id="problem_description"
                value={formData.problem_description}
                onChange={(e) => setFormData({...formData, problem_description: e.target.value})}
                rows={4}
                placeholder="Describe what you found when you arrived on site..."
                className={errors.problem_description ? 'border-red-500' : ''}
              />
              {errors.problem_description && (
                <p className="text-red-500 text-sm mt-1">{errors.problem_description}</p>
              )}
            </div>

            {/* Resolution Summary */}
            <div>
              <Label htmlFor="resolution_summary">Work Performed / Resolution *</Label>
              <Textarea
                id="resolution_summary"
                value={formData.resolution_summary}
                onChange={(e) => setFormData({...formData, resolution_summary: e.target.value})}
                rows={4}
                placeholder="Describe the work you performed and how the issue was resolved..."
                className={errors.resolution_summary ? 'border-red-500' : ''}
              />
              {errors.resolution_summary && (
                <p className="text-red-500 text-sm mt-1">{errors.resolution_summary}</p>
              )}
            </div>

            {/* Materials Used */}
            <div>
              <Label htmlFor="materials_used">Materials / Parts Used</Label>
              <Input
                id="materials_used"
                value={formData.materials_used}
                onChange={(e) => setFormData({...formData, materials_used: e.target.value})}
                placeholder="e.g., 2x outlet covers, 1x light bulb"
              />
            </div>

            {/* Billable Status */}
            <div>
              <Label>Is This Work Billable? *</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={formData.is_billable === true}
                    onChange={() => setFormData({...formData, is_billable: true})}
                    className="mr-2"
                  />
                  Yes - Billable
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={formData.is_billable === false}
                    onChange={() => setFormData({...formData, is_billable: false})}
                    className="mr-2"
                  />
                  No - Not Billable
                </label>
              </div>
            </div>

            {/* Before Photos */}
            <div>
              <Label>Before Photos * (Required)</Label>
              <p className="text-sm text-gray-600 mb-2">Upload at least one photo showing conditions before work</p>
              
              <div className="flex flex-wrap gap-2 mb-2">
                {beforePhotoURLs.map((url, index) => (
                  <div key={index} className="relative">
                    <img src={url} alt={`Before ${index + 1}`} className="w-24 h-24 object-cover rounded" />
                    <button
                      type="button"
                      onClick={() => removeBeforePhoto(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
              
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleBeforePhotoChange}
                className="hidden"
                id="before-photos"
              />
              <label
                htmlFor="before-photos"
                className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:bg-gray-50 ${
                  errors.before_photos ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <Camera size={20} />
                <span>Add Before Photos</span>
              </label>
              {errors.before_photos && (
                <div className="flex items-center gap-2 text-red-500 text-sm mt-2">
                  <AlertCircle size={16} />
                  <span>{errors.before_photos}</span>
                </div>
              )}
            </div>

            {/* After Photos */}
            <div>
              <Label>After Photos * (Required)</Label>
              <p className="text-sm text-gray-600 mb-2">Upload at least one photo showing completed work</p>
              
              <div className="flex flex-wrap gap-2 mb-2">
                {afterPhotoURLs.map((url, index) => (
                  <div key={index} className="relative">
                    <img src={url} alt={`After ${index + 1}`} className="w-24 h-24 object-cover rounded" />
                    <button
                      type="button"
                      onClick={() => removeAfterPhoto(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
              
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleAfterPhotoChange}
                className="hidden"
                id="after-photos"
              />
              <label
                htmlFor="after-photos"
                className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:bg-gray-50 ${
                  errors.after_photos ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <Camera size={20} />
                <span>Add After Photos</span>
              </label>
              {errors.after_photos && (
                <div className="flex items-center gap-2 text-red-500 text-sm mt-2">
                  <AlertCircle size={16} />
                  <span>{errors.after_photos}</span>
                </div>
              )}
            </div>

            {/* Customer Satisfaction */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-lg mb-4">Customer Satisfaction (Optional)</h3>
              
              <div>
                <Label>Overall Satisfaction Rating</Label>
                <p className="text-sm text-gray-600 mb-2">If customer is available, ask for their satisfaction rating</p>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setFormData({...formData, satisfaction_rating: rating})}
                      className={`flex flex-col items-center p-2 rounded border ${
                        formData.satisfaction_rating === rating
                          ? 'bg-yellow-100 border-yellow-500'
                          : 'bg-white border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Star
                        size={24}
                        className={formData.satisfaction_rating >= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                      />
                      <span className="text-xs mt-1">{rating}</span>
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1 px-2">
                  <span>Poor</span>
                  <span>Excellent</span>
                </div>
              </div>

              {formData.satisfaction_rating > 0 && (
                <div className="mt-4">
                  <Label htmlFor="satisfaction_comments">Customer Comments (Optional)</Label>
                  <Textarea
                    id="satisfaction_comments"
                    value={formData.satisfaction_comments}
                    onChange={(e) => setFormData({...formData, satisfaction_comments: e.target.value})}
                    rows={3}
                    placeholder="Any additional feedback from the customer..."
                  />
                </div>
              )}
            </div>

            {/* Customer Representative */}
            <div className="border-t pt-6">
              <h3 className="font-semibold text-lg mb-4">Customer Acknowledgment</h3>
              
              <div>
                <Label htmlFor="customer_representative_name">Customer Representative Name</Label>
                <Input
                  id="customer_representative_name"
                  value={formData.customer_representative_name}
                  onChange={(e) => setFormData({...formData, customer_representative_name: e.target.value})}
                  placeholder="Name of person acknowledging completion"
                />
              </div>

              {/* Signature Status */}
              <div className="mt-4">
                <Label htmlFor="signature_status">Signature Status *</Label>
                <Select
                  value={formData.signature_status}
                  onValueChange={(value) => setFormData({...formData, signature_status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Signed by Customer Representative">Signed by Customer Representative</SelectItem>
                    <SelectItem value="Not Available at Time Work Was Performed">Not Available at Time Work Was Performed</SelectItem>
                    <SelectItem value="Not Required">Not Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Signature Canvas */}
              {formData.signature_status === 'Signed by Customer Representative' && (
                <div className="mt-4">
                  <Label>Customer Signature *</Label>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                    <canvas
                      ref={signatureCanvasRef}
                      width={600}
                      height={200}
                      className="w-full bg-white cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button type="button" variant="outline" size="sm" onClick={clearSignature}>
                      Clear Signature
                    </Button>
                    {hasSignature && (
                      <div className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle size={16} />
                        <span>Signature captured</span>
                      </div>
                    )}
                  </div>
                  {errors.signature && (
                    <p className="text-red-500 text-sm mt-1">{errors.signature}</p>
                  )}
                </div>
              )}
            </div>

            {/* Field User Notes */}
            <div>
              <Label htmlFor="field_user_notes">Additional Notes (Optional)</Label>
              <Textarea
                id="field_user_notes"
                value={formData.field_user_notes}
                onChange={(e) => setFormData({...formData, field_user_notes: e.target.value})}
                rows={3}
                placeholder="Any additional notes or observations..."
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#A8D5BA] hover:bg-[#8FC9A9] text-gray-800 flex-1"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Complete Work Order'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkOrderCompletionForm;
